"use client";

import React from "react";
import Link from "next/link";
import "./ErrorPage.css";

export type ErrorCode = 400 | 401 | 403 | 404 | 500 | 501;

type Action =
    | { kind: "back"; label: string }
    | { kind: "link"; label: string; href: string }
    | { kind: "retry"; label: string; onRetry?: () => void };

type Props = {
    code: ErrorCode;
    title: string;
    message: React.ReactNode;
    accent: string;
    background: string;
    messageColor?: string;

    /**
     * If provided, we render the circle + icon in the "standard" spot.
     * Use this for 400/401/403/500/501 when the icon sits in the circle.
     */
    icon?: React.ReactNode;

    /**
     * If provided, this gets rendered inside the root container and can be
     * absolutely positioned (great for 404 exported vectors that are page-positioned).
     */
    overlay?: React.ReactNode;

    primaryAction: Action;
    secondaryAction?: Action;
};

export default function ErrorPage({
    title,
    message,
    accent,
    background,
    messageColor = "#000",
    icon,
    overlay,
    primaryAction,
    secondaryAction,
}: Props) {
    const handlePrimary = () => {
        if (primaryAction.kind === "back") window.history.back();
        if (primaryAction.kind === "retry") primaryAction.onRetry?.();
    };

    const primaryButtonStyle: React.CSSProperties = {
        backgroundColor: accent,
        color: "var(--error-page-button-text, #fff)",
        border: "none",
        borderRadius: 8,
        height: 47,
        width: 257,
        fontSize: 20,
        fontWeight: 700,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textDecoration: "none",
    };

    const secondaryLinkStyle: React.CSSProperties = {
        color: accent,
        fontSize: 20,
        fontWeight: 700,
        textDecoration: "none",
        cursor: "pointer",
        backgroundColor: "transparent",
        border: "none",
        padding: 0,
    };

    const renderAction = (action: Action, isPrimary = false) => {
        if (action.kind === "link") {
            return (
                <Link
                    href={action.href}
                    style={isPrimary ? primaryButtonStyle : secondaryLinkStyle}
                >
                    {action.label}
                </Link>
            );
        }

        return (
            <button
                type="button"
                onClick={
                    isPrimary ? handlePrimary : () => window.history.back()
                }
                style={isPrimary ? primaryButtonStyle : secondaryLinkStyle}
            >
                {action.label}
            </button>
        );
    };

    return (
        <div
            className="error-page-root"
            style={{
                position: "relative",
                width: "100%",
                height: "100vh",
                minHeight: 800,
                fontFamily:
                    '"DM Sans", system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
                overflow: "hidden",
                ["--error-page-background" as string]: background,
            }}
        >
            {/* Optional overlay for page-positioned vectors (ex: 404 magnifier + X) */}
            {overlay}

            {/* Optional circle + icon (standard layout) */}
            {icon && (
                <>
                    {/* Circle w/ shadow */}
                    <div
                        style={{
                            position: "absolute",
                            left: "50%",
                            top: 171,
                            transform: "translateX(-50%)",
                            width: 156,
                            height: 156,
                        }}
                    >
                        <div
                            style={{
                                position: "absolute",
                                inset: "-1.35% -7.76% -7.76% -1.35%",
                            }}
                        >
                            <svg
                                fill="none"
                                preserveAspectRatio="none"
                                viewBox="0 0 171 171"
                                style={{
                                    display: "block",
                                    width: "100%",
                                    height: "100%",
                                }}
                            >
                                <g filter="url(#filter0_d_errorpage)">
                                    <circle
                                        cx="80.1"
                                        cy="80.1"
                                        fill="var(--error-page-circle, #ffffff)"
                                        r="78"
                                    />
                                </g>
                                <defs>
                                    <filter
                                        id="filter0_d_errorpage"
                                        x="0"
                                        y="0"
                                        width="170.2"
                                        height="170.2"
                                        filterUnits="userSpaceOnUse"
                                        colorInterpolationFilters="sRGB"
                                    >
                                        <feFlood
                                            floodOpacity="0"
                                            result="BackgroundImageFix"
                                        />
                                        <feColorMatrix
                                            in="SourceAlpha"
                                            result="hardAlpha"
                                            type="matrix"
                                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                        />
                                        <feOffset dx="5" dy="5" />
                                        <feGaussianBlur stdDeviation="3.55" />
                                        <feComposite
                                            in2="hardAlpha"
                                            operator="out"
                                        />
                                        <feColorMatrix
                                            type="matrix"
                                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                                        />
                                        <feBlend
                                            in2="BackgroundImageFix"
                                            mode="normal"
                                            result="effect1_dropShadow"
                                        />
                                        <feBlend
                                            in="SourceGraphic"
                                            in2="effect1_dropShadow"
                                            mode="normal"
                                            result="shape"
                                        />
                                    </filter>
                                </defs>
                            </svg>
                        </div>
                    </div>

                    {/* Icon centered in circle */}
                    <div
                        style={{
                            position: "absolute",
                            left: "50%",
                            top: 200,
                            transform: "translateX(-50%)",
                            width: 91,
                            height: 91,
                        }}
                    >
                        {icon}
                    </div>
                </>
            )}

            {/* Title */}
            <div
                style={{
                    position: "absolute",
                    top: 365,
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: accent,
                    fontSize: 34,
                    fontWeight: 600,
                    lineHeight: "48px",
                    textAlign: "center",
                    width: 737,
                    maxWidth: "90%",
                    fontVariationSettings: "'opsz' 14",
                }}
            >
                {title}
            </div>

            {/* Message (ReactNode safe, supports 1–2 lines) */}
            <div
                className="error-page-message"
                style={{
                    position: "absolute",
                    top: 443,
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: 24,
                    fontWeight: 400,
                    lineHeight: "33px",
                    textAlign: "center",
                    width: 551,
                    maxWidth: "90%",
                    fontVariationSettings: "'opsz' 14",
                    ["--error-message-color" as string]: messageColor,
                }}
            >
                {message}
            </div>

            {/* Primary Action */}
            <div
                style={{
                    position: "absolute",
                    top: 626,
                    left: "50%",
                    transform: "translateX(-50%)",
                }}
            >
                {renderAction(primaryAction, true)}
            </div>

            {/* Secondary Action */}
            {secondaryAction && (
                <div
                    style={{
                        position: "absolute",
                        top: 718,
                        left: "50%",
                        transform: "translateX(-50%)",
                    }}
                >
                    {renderAction(secondaryAction, false)}
                </div>
            )}
        </div>
    );
}
